// @ts-strict-ignore
import {Component, effect, inject, input, OnInit, viewChildren} from "@angular/core";
import {Translations} from "@translations";
import {FetchDataType, IId, SearchableColumn, SearchFilter, SelectableOption} from "@models";
import {AdministrationTableService} from "../administration-table.service";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {debounceTime, map} from "rxjs/operators";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {AdministrationTableFormService} from "../administration-table-form.service";
import {SimChipAutocompleteComponent} from "../../sim-advanced-inputs/autocomplete/sim-chip-autocomplete/sim-chip-autocomplete.component";
import {FetchData_TypeMapping} from "../../sim-advanced-inputs/autocomplete/autocomplete.service";
import {MatSelect, MatSelectChange} from "@angular/material/select";
import {FormControl} from "@angular/forms";
import {TypePredicates} from "@services";

type FilterAction = {
	state: "hidden";
};

type EnabledFilterAction = {
	state: "enabled" | "disabled";
	action: () => void;
};

export type FilterActions = {
	download: FilterAction | EnabledFilterAction;
};

@UntilDestroy()
@Component({
	selector: "sim-administration-table-advanced-search",
	templateUrl: "./administration-table-advanced-search.component.html",
	styleUrl: "./administration-table-advanced-search.component.scss",
	animations: [zoomInOnEnterAnimation({duration: 175}), zoomOutOnLeaveAnimation({duration: 175})],
	standalone: false,
})
export class AdministrationTableAdvancedSearchComponent<T extends IId> implements OnInit {
	protected readonly T = Translations;
	protected readonly FetchDataType = FetchDataType;
	protected readonly TypePredicates = TypePredicates;

	protected stateService = inject<AdministrationTableService<T>>(AdministrationTableService);
	protected formService = inject(AdministrationTableFormService);

	protected formGroup = this.formService.formGroup;

	public filterActions = input.required<FilterActions>();
	public searchableColumns = input.required<SearchableColumn[]>();

	private _autoCompleteComponents = viewChildren<SimChipAutocompleteComponent<keyof FetchData_TypeMapping>>(SimChipAutocompleteComponent);

	private _registerAutoCompleteComponentsEffect = effect(() => {
		for (const autoCompleteComponent of this._autoCompleteComponents()) {
			this.formService.registerAutoCompleteComponent(autoCompleteComponent, autoCompleteComponent.id);
		}
	});

	public ngOnInit() {
		this.formService.initializeForm(this.searchableColumns(), this.stateService.searchFilters()).then(() => {
			this.formService.formGroup.valueChanges
				.pipe(
					untilDestroyed(this),
					debounceTime(500),
					map((form) => this._buildSearchFilter(form)),
				)
				.subscribe((filter) => {
					this.stateService.setSearchFilter(filter);
				});
		});
	}

	public executeFilterAction(action: (results: T[]) => void) {
		action(this.stateService.dataSource());
	}

	private _buildSearchFilter(filterForm: any): SearchFilter[] {
		const advancedSearchFilter: SearchFilter[] = [];

		this.searchableColumns().forEach((col) => {
			const rawValue = filterForm[col.value];
			const ifIsArrayThenNonEmpty = Array.isArray(rawValue) ? rawValue.length > 0 : true;
			if (ifIsArrayThenNonEmpty && rawValue !== null && rawValue !== undefined && rawValue !== "") {
				advancedSearchFilter.push({
					searchTerms: this._convertSearchableColumnToSearchTerms(col.type, rawValue),
					searchColumn: col.value,
					searchOperator: col.searchOperator,
					exact: Boolean(col.exactMatch),
				});
			}
		});

		return advancedSearchFilter;
	}

	// TODO: move this to SearchableColumn and replace the old function
	private _convertSearchableColumnToSearchTerms(type: SearchableColumn["type"], rawValue: unknown): string[] {
		if (type === "Date") {
			if (rawValue instanceof Date && !isNaN(rawValue.getTime())) {
				let year = rawValue.getFullYear();
				let month = String(rawValue.getMonth() + 1).padStart(2, "0");
				let day = String(rawValue.getDate()).padStart(2, "0");
				return [`${year}-${month}-${day}`];
			} else {
				return null;
			}
		} else if (type === "Checkbox" || type === "FreeText" || type === "Select") {
			return [rawValue?.toString()];
		} else if (type === "MultiSelect") {
			return (rawValue as string[]) || null;
		} else {
			return [(rawValue as IId)?.id];
		}
	}

	protected addMultiselectEntry(col: SearchableColumn, matSelectEvent: MatSelectChange, matSelect: MatSelect): void {
		const control = this.formGroup.get(col.value) as FormControl<string[]>;
		const newValue = [...new Set([...control.value, matSelectEvent.value])];

		control.setValue(newValue);
		matSelect.value = null;
	}

	protected removeMultiSelectEntry(col: SearchableColumn, multiSelectValue: string): void {
		const control = this.formGroup.get(col.value) as FormControl<string[]>;
		const newValue = control.value.filter((x) => x !== multiSelectValue);
		control.setValue(newValue);
	}

	protected findMultiselectValue(col: SelectableOption[], multiSelectValue: string): SelectableOption {
		return col.find((x) => x.value === multiSelectValue);
	}
}
